import { useMemo } from 'react';

import {
  Button,
  Icons,
  InfoTooltip,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { NonEmptyArray } from '@cast/shared-types';

import { Money } from 'components/money';

import { SummaryItem } from './SummaryItem';
import {
  binPackingBlockedCriterion,
  BinPackingBlockedFilterValue,
} from '../../../constants';
import { PreparedNode } from '../../../types';
import { calculateNodesMonthlyCost } from '../../../utils';

import { useSearchCriterion } from '@cast/shared-search-ui';

type Props = {
  binPackingBlockedNodes: NonEmptyArray<PreparedNode>;
};

export const BinPackingBlockedNodesSummary = ({
  binPackingBlockedNodes,
}: Props) => {
  const { setValue } = useSearchCriterion(binPackingBlockedCriterion);
  const nodesMonthlyCost = useMemo(() => {
    return calculateNodesMonthlyCost(binPackingBlockedNodes);
  }, [binPackingBlockedNodes]);

  return (
    <SummaryItem
      testId="nodes-summary-binpacking-blocked"
      sx={{
        backgroundColor: tokens.theme.bg.status.warningSubtle,
        border: '1px solid',
        borderColor: tokens.theme.border.status.warningSubtle,
        color: tokens.theme.fg.status.onWarningSubtle,
      }}
    >
      <Stack gap={16}>
        <Stack gap={4}>
          <Stack direction="row" gap={4} alignItems="center">
            <Typography variant="L10R">Bin packing blocked</Typography>
            <InfoTooltip title="Certain workloads and nodes can not be considered for eviction based on their current configurations. Resolving these blockers can unlock additional savings." />
          </Stack>
          <Stack direction="row" gap={4} alignItems="baseline">
            <Typography
              variant="h4"
              sx={{ color: tokens.theme.fg.status.onWarningSubtle }}
              data-testid="nodes-summary-binpacking-blocked-count"
            >
              {binPackingBlockedNodes.length}
            </Typography>
            <Typography
              variant="P10R"
              sx={{ color: tokens.theme.fg.status.onWarningSubtle }}
            >
              nodes
            </Typography>
          </Stack>
        </Stack>
        <Stack gap={4}>
          <Stack direction="row" gap={8} alignItems="baseline">
            <Typography variant="L10R" textTransform="uppercase">
              Cost of the nodes
            </Typography>
            <Stack
              direction="row"
              gap={4}
              alignItems="baseline"
              data-testid="nodes-summary-binpacking-blocked-cost"
            >
              <Typography variant="A2">
                <Money amount={nodesMonthlyCost} />
              </Typography>
              <Typography variant="P10M">/mo</Typography>
            </Stack>
          </Stack>
          <Button
            data-testid="nodes-summary-view-problematic-nodes-button"
            variant="text"
            size="sm"
            endIcon={<Icons.ArrowRight size={12} />}
            onClick={() => {
              setValue(BinPackingBlockedFilterValue.YES);
            }}
          >
            View problematic nodes
          </Button>
        </Stack>
      </Stack>
    </SummaryItem>
  );
};
